import React, { useEffect, useState } from 'react';
import { Apple, Home } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface ProgressData {
  status: 'processing' | 'completed' | 'error';
  currentStep: number;
  totalSteps: number;
  currentAction: string;
  message: string;
  percentage: number;
  timestamp: string;
}

interface FoodProgressModalProps {
  isOpen: boolean;
  onComplete: () => void;
  onError: (error: string) => void;
  onDismiss?: () => void;
}

export const FoodProgressModal: React.FC<FoodProgressModalProps> = ({
  isOpen,
  onComplete,
  onError,
  onDismiss
}) => {
  const navigate = useNavigate();
  const [progress, setProgress] = useState<ProgressData>({
    status: 'processing',
    currentStep: 0,
    totalSteps: 3,
    currentAction: '',
    message: 'Pregătesc generarea...',
    percentage: 0,
    timestamp: new Date().toISOString()
  });

  useEffect(() => {
    if (!isOpen) return;

    let retryCount = 0;
    const maxRetries = 3;
    let currentInterval = 2000; // Start cu 2 secunde

    const pollProgress = async () => {
      try {
        const token = localStorage.getItem('accessToken');
        const response = await fetch(
          `${import.meta.env.VITE_API_URL}/api/nutrition/foods/progress?token=${token}`
        );
        
        if (response.status === 429) {
          retryCount++;
          if (retryCount <= maxRetries) {
            currentInterval = Math.min(currentInterval * 2, 8000);
            console.log(`Rate limit hit, backing off to ${currentInterval}ms`);
            return false;
          } else {
            onError('Prea multe cereri. Te rog reîncearcă mai târziu.');
            return true;
          }
        }
        
        if (response.ok) {
          const data: ProgressData = await response.json();
          setProgress(data);
          
          retryCount = 0;
          currentInterval = 2000;

          if (data.status === 'completed') {
            setTimeout(() => onComplete(), 1000);
            return true;
          } else if (data.status === 'error') {
            onError(data.message);
            return true;
          }
        } else if (response.status === 401) {
          retryCount++;
          if (retryCount > maxRetries) {
            onError('Sesiunea a expirat. Te rog reautentifică-te și încearcă din nou.');
            return true;
          }
          return false;
        } else if (response.status === 402) {
          const data = await response.json().catch(() => ({}));
          const available = data.available ?? 0;
          const required = data.required ?? 0;
          onError(`Nu ai suficiente credite! Ai ${available} credite disponibile, dar ai nevoie de ${required}. Achiziționează mai multe credite.`);
          return true;
        }
      } catch (error) {
        console.error('Error fetching progress:', error);
      }
      return false;
    };

    let intervalId: ReturnType<typeof setTimeout> | undefined;
    
    const startPolling = async () => {
      const shouldStop = await pollProgress();
      if (!shouldStop) {
        intervalId = setTimeout(startPolling, currentInterval);
      }
    };

    setTimeout(startPolling, 500);

    return () => {
      if (intervalId) {
        clearTimeout(intervalId);
      }
    };
  }, [isOpen, onComplete, onError]);

  if (!isOpen) return null;

  const steps = [
    '✅ Analizare profil fitness',
    '🔄 Consultare ghiduri nutriționale',
    '⏳ Generare listă personalizată'
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-2xl max-w-2xl w-full p-4 md:p-8">
        {/* Header */}
        <div className="text-center mb-4 md:mb-6">
          <div className="w-12 h-12 md:w-16 md:h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3 md:mb-4">
            <Apple className="w-6 h-6 md:w-8 md:h-8 text-green-600" />
          </div>
          <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-1 md:mb-2">
            Generez lista de alimente
          </h2>
          <p className="text-sm md:text-base text-gray-600">
            Te rog așteaptă, analizez ghidurile nutriționale
          </p>
        </div>

        {/* Progress Bar */}
        <div className="mb-4 md:mb-8">
          <div className="flex justify-between text-sm text-gray-600 mb-2">
            <span>Progres</span>
            <span className="font-semibold">{progress.percentage}%</span>
          </div>
          <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-green-500 to-blue-600 transition-all duration-500 ease-out"
              style={{ width: `${progress.percentage}%` }}
            />
          </div>
        </div>

        {/* Current Status with Details */}
        <div className="bg-green-50 border border-green-200 rounded-lg p-3 md:p-4 mb-4 md:mb-6">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 mt-1">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-green-600"></div>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-green-900 mb-1">
                {progress.message}
              </p>
              {progress.currentAction && (
                <div className="mt-2 p-2 bg-white rounded border border-green-100">
                  <p className="text-xs text-green-800 font-mono break-words">
                    {progress.currentAction}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Steps List - Optimizat pentru mobil */}
        <div className="mb-4 md:mb-6">
          {/* Desktop: Layout vertical clasic */}
          <div className="hidden md:block space-y-2">
            {steps.map((step, index) => {
              const isCompleted = index < progress.currentStep;
              const isCurrent = index === progress.currentStep;

              return (
                <div
                  key={index}
                  className={`flex items-center justify-between p-3 rounded-lg border transition-colors ${
                    isCompleted
                      ? 'bg-green-50 border-green-200'
                      : isCurrent
                      ? 'bg-green-50 border-green-200'
                      : 'bg-gray-50 border-gray-200'
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    <div
                      className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${
                        isCompleted
                          ? 'bg-green-500 text-white'
                          : isCurrent
                          ? 'bg-green-500 text-white'
                          : 'bg-gray-300 text-gray-600'
                      }`}
                    >
                      {index + 1}
                    </div>
                    <span
                      className={`font-medium ${
                        isCompleted
                          ? 'text-green-900'
                          : isCurrent
                          ? 'text-green-900'
                          : 'text-gray-600'
                      }`}
                    >
                      {step}
                    </span>
                  </div>
                  <span
                    className={`text-sm ${
                      isCompleted
                        ? 'text-green-700'
                        : isCurrent
                        ? 'text-green-700'
                        : 'text-gray-500'
                    }`}
                  >
                    {isCompleted ? 'Completat' : isCurrent ? 'În procesare' : 'În așteptare'}
                  </span>
                </div>
              );
            })}
          </div>

          {/* Mobile: Grid compact 3 coloane */}
          <div className="md:hidden">
            <div className="grid grid-cols-3 gap-2 mb-3">
              {steps.map((step, index) => {
                const isCompleted = index < progress.currentStep;
                const isCurrent = index === progress.currentStep;

                return (
                  <div
                    key={index}
                    className={`flex flex-col items-center p-2 rounded-lg border transition-colors ${
                      isCompleted
                        ? 'bg-green-50 border-green-200'
                        : isCurrent
                        ? 'bg-green-50 border-green-200'
                        : 'bg-gray-50 border-gray-200'
                    }`}
                  >
                    <div
                      className={`w-10 h-10 rounded-full flex items-center justify-center text-lg font-semibold mb-1 ${
                        isCompleted
                          ? 'bg-green-500 text-white'
                          : isCurrent
                          ? 'bg-green-500 text-white'
                          : 'bg-gray-300 text-gray-600'
                      }`}
                    >
                      {index + 1}
                    </div>
                    <span className="text-xl">{step.split(' ')[0]}</span>
                  </div>
                );
              })}
            </div>
            <p className="text-center text-xs text-gray-600">
              {progress.currentStep === 0 && 'Analizare profil fitness'}
              {progress.currentStep === 1 && 'Consultare ghiduri nutriționale'}
              {progress.currentStep === 2 && 'Generare listă personalizată'}
              {progress.currentStep >= 3 && 'Finalizare...'}
            </p>
          </div>
        </div>

        {/* Time Estimate */}
        <div className="text-center text-xs md:text-sm text-gray-600 mb-4 md:mb-6">
          <p>
            Timp estimat rămas:{' '}
            <span className="font-semibold text-gray-900">
              {progress.currentStep < 2 ? '~30 secunde' : '~10 secunde'}
            </span>
          </p>
          <p className="text-xs text-gray-500 mt-2">
            Generez liste personalizate de alimente permise și interzise
          </p>
        </div>

        {/* Dismiss Button */}
        <div className="border-t pt-4 md:pt-6">
          <button
            onClick={() => {
              if (onDismiss) {
                onDismiss();
              } else {
                navigate('/dashboard');
              }
            }}
            className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors"
          >
            <Home className="w-5 h-5" />
            Înapoi la Dashboard
          </button>
          <p className="text-xs text-center text-gray-500 mt-3">
            💡 Procesul continuă în background. Poți reveni mai târziu să vezi rezultatele.
          </p>
        </div>
      </div>
    </div>
  );
};
